// CLARTEXT · /abo · B2-B
// Schlanker Seiten-Hook der Abo-Produktseite.
//
// STRICT SCOPE:
//   1. Abo-Preis kommt AUSSCHLIESSLICH aus GET /api/catalog (autoritativ).
//      Kein hardcodierter Preis, kein erfundener Fallback. Zustände:
//      "loading" → "ready" | "unavailable".
//   2. Trial-Start über den bestehenden Self-Service-Endpunkt
//      POST /public/demo-start-wizard — exakt dasselbe Aufruf-/Redirect-Muster
//      wie useAbschlussKauffluss.startTrial (24h-Browser-Sperre, credentials,
//      Einmal-Zugangsdaten via localStorage, Redirect /unternehmer). Der
//      Trial-Vertrag im Backend bleibt UNVERÄNDERT.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API, api } from "@/lib/api";
import {
  formatEuro,
  ABSCHLUSS_TOPIC_KEY,
  ABSCHLUSS_CONTENT_KEY,
} from "@/components/abschluss/useAbschlussKauffluss";
import { erfasseHerkunft, mitHerkunft } from "@/lib/herkunft"; // MESS-07, Betreiberfreigabe 27.10.2026

// Clientseitige 24h-Browser-Sperre — identisch zu useAbschlussKauffluss /
// ZweiTuerenPopup (Backend-IP-Limit greift zusätzlich).
const DEMO_LAST_KEY = "clartext_lp_demo_at";
const DAY_MS = 24 * 60 * 60 * 1000;

export function useAboSeite() {
  // "loading" | "ready" | "unavailable" — steuert Skeleton vs. echter Preis.
  const [priceState, setPriceState] = useState("loading");
  const [monthlyCents, setMonthlyCents] = useState(null);
  const [yearlyCents, setYearlyCents] = useState(null);
  const [currency, setCurrency] = useState("EUR");
  const [trialLoading, setTrialLoading] = useState(false);
  const [trialError, setTrialError] = useState(null);
  const trialBusyRef = useRef(false);
  // B2-E3 · Wertanker: einfacher Mitarbeiterzahl-Selector (neutraler Startwert 5,
  // Betreiberentscheidung — KEIN Default 6). Der Betriebspreis wird rein
  // mathematisch aus dem Live-Katalogpreis berechnet, nichts hart kodiert.
  const [employeeCount, setEmployeeCount] = useState(5);
  // Tätigkeitsbegriff der zuvor freigegebenen Vorschau (bestehender Vertrag
  // clartext_abschluss_topic) für Chris-Moment 2. Leer = neutraler Fallback.
  const [topic, setTopic] = useState("");

  useEffect(() => {
    erfasseHerkunft(window.location.search);
    try {
      const raw = (window.sessionStorage.getItem(ABSCHLUSS_TOPIC_KEY) || "")
        .replace(/\s+/g, " ")
        .trim();
      if (raw && raw.length <= 48) setTopic(raw);
    } catch {
      /* sessionStorage optional → Fallback-Text */
    }
  }, []);

  // Abo-Preis laden — Produkt mit per_employee/subscription aus /api/catalog.
  useEffect(() => {
    let alive = true;
    api
      .getPublicCatalog()
      .then((data) => {
        if (!alive) return;
        const products =
          data && Array.isArray(data.products) ? data.products : [];
        const abo = products.find(
          (p) =>
            p.pricing &&
            (p.pricing.per_employee === true ||
              p.pricing.product_type === "subscription"),
        );
        if (abo && abo.pricing && abo.pricing.monthly_net_cents != null) {
          setMonthlyCents(abo.pricing.monthly_net_cents);
          setYearlyCents(abo.pricing.yearly_net_cents ?? null);
          setCurrency(abo.pricing.currency || "EUR");
          setPriceState("ready");
        } else {
          setPriceState("unavailable");
        }
      })
      .catch(() => {
        if (alive) setPriceState("unavailable");
      });
    return () => {
      alive = false;
    };
  }, []);

  const monthlyText = useMemo(
    () => (monthlyCents == null ? null : formatEuro(monthlyCents)),
    [monthlyCents],
  );
  const yearlyText = useMemo(
    () => (yearlyCents == null ? null : formatEuro(yearlyCents)),
    [yearlyCents],
  );
  const currencySymbol = currency === "EUR" ? "€" : currency;

  // Betriebspreis = Mitarbeiterzahl × Live-Monatspreis (netto). Nur abgeleitet.
  const totalCents = useMemo(
    () => (monthlyCents == null ? null : monthlyCents * employeeCount),
    [monthlyCents, employeeCount],
  );
  const totalText = useMemo(
    () => (totalCents == null ? null : formatEuro(totalCents)),
    [totalCents],
  );

  const startTrial = useCallback(async () => {
    if (trialBusyRef.current) return;
    try {
      const last = Number(localStorage.getItem(DEMO_LAST_KEY) || 0);
      if (last && Date.now() - last < DAY_MS) {
        setTrialError(
          "Sie haben kürzlich bereits einen Testzugang gestartet. Bitte versuchen Sie es später erneut.",
        );
        return;
      }
    } catch {
      /* localStorage optional — Backend-Limit greift trotzdem */
    }
    trialBusyRef.current = true;
    setTrialLoading(true);
    setTrialError(null);

    // Tätigkeitsbegriff der zuvor freigegebenen Vorschau (falls vorhanden) für
    // die KI-Welt-Generierung des frischen Demo-Mandanten mitgeben.
    let topic = "";
    try {
      topic = (window.sessionStorage.getItem(ABSCHLUSS_TOPIC_KEY) || "").trim();
    } catch {
      /* sessionStorage optional */
    }
    if (!topic) {
      try {
        const raw = window.sessionStorage.getItem(ABSCHLUSS_CONTENT_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed.title === "string") {
            topic = parsed.title.trim();
          }
        }
      } catch {
        /* Content optional */
      }
    }

    try {
      const res = await fetch(`${API}/public/demo-start-wizard`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(mitHerkunft(topic ? { topic } : {})),
      });
      if (!res.ok) {
        trialBusyRef.current = false;
        setTrialLoading(false);
        setTrialError(
          res.status === 429
            ? "Zu viele Testzugänge von dieser Verbindung. Bitte versuchen Sie es morgen erneut."
            : "Der Testzugang konnte nicht gestartet werden. Bitte versuchen Sie es später erneut.",
        );
        return;
      }
      const data = await res.json();
      try {
        localStorage.setItem(
          "owner_session",
          JSON.stringify({
            tenant_id: data.tenant_id,
            company_name: data.company_name,
            company_code: data.company_code,
            must_change_pin: !!data.must_change_pin,
          }),
        );
        if (data.owner_pin) {
          localStorage.setItem(
            "pending_demo_creds",
            JSON.stringify({
              demo_tenant_id: data.tenant_id,
              company_code: data.company_code,
              owner_pin: data.owner_pin,
            }),
          );
        }
        localStorage.setItem(DEMO_LAST_KEY, String(Date.now()));
      } catch {
        /* Redirect erfolgt trotzdem */
      }
      window.location.href = "/unternehmer";
    } catch {
      trialBusyRef.current = false;
      setTrialLoading(false);
      setTrialError("Verbindungsproblem. Bitte versuchen Sie es später erneut.");
    }
  }, []);

  return {
    priceState,
    monthlyCents,
    monthlyText,
    yearlyText,
    currencySymbol,
    employeeCount,
    setEmployeeCount,
    totalCents,
    totalText,
    topic,
    startTrial,
    trialLoading,
    trialError,
  };
}
